import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { TTableTreeModel } from '../table/models';
import { ChatUiModelModifiers } from './model/chat-ui.modifier';
import {
  TChatUiComponentUpdateEvent,
  TChatUiModel,
  TChatUiModelFeedback,
  TChatUiQuery,
  TChatUiTableAndChartResponseData,
} from './model/chat-ui.types';

@Component({
  selector: 'monorepo-chat-ui',
  templateUrl: './chat-ui.component.html',
  styleUrls: ['./chat-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatUiComponent<T> implements AfterViewInit {
  @ViewChild('chatHistoryContainer') chatHistoryContainerRef?: ElementRef;
  @ViewChild('sendBtn', { read: ElementRef }) sendBtn!: ElementRef;

  @Output() chatUiUpdateEvent = new EventEmitter<TChatUiComponentUpdateEvent<T>>();

  @Input() set zChatUiModel(value: TChatUiModel<T>) {
    this.chatUiModel.set(value);
  }
  @Input() initialUiText?: { header: string; content?: string } = undefined;
  @Input() quickQuestions?: string[] = undefined;

  chatUiModel = signal(ChatUiModelModifiers.createIdentityModel<T>());
  nonSeparatorChatsCount = computed(() => this.chatUiModel().chats.filter((c) => c.type !== 'SEPARATOR').length);

  #scrollToBottom() {
    setTimeout(() => {
      if (this.chatHistoryContainerRef && this.chatHistoryContainerRef.nativeElement)
        this.chatHistoryContainerRef.nativeElement.scrollTo({
          top: this.chatHistoryContainerRef.nativeElement.scrollHeight,
          behavior: 'smooth',
        });
    }, 1500);
  }

  ngAfterViewInit() {
    this.#scrollToBottom();

    setTimeout(() => {
      this.sendBtn.nativeElement.focus();
    }, 1000);
  }

  onQueryChange($event: Event) {
    const updatedQuery: string | undefined = ($event.target as HTMLInputElement)?.value ?? '';
    this.chatUiModel.update((prevModel) => ChatUiModelModifiers.updateQueryInModel(prevModel, updatedQuery));
  }

  onSendQuery() {
    const newQuery: TChatUiQuery = {
      value: this.chatUiModel().queryValue,
      type: 'QUERY',
    };

    this.#scrollToBottom();

    this.chatUiUpdateEvent.emit({
      type: 'QUERY_SEND',
      query: newQuery,
      chatUiModel: ChatUiModelModifiers.addNewChat(this.chatUiModel(), newQuery),
    });
  }

  onTabClick(response: TChatUiTableAndChartResponseData, index: number, tab: TChatUiTableAndChartResponseData['tab']) {
    if (response.tab === tab) return;

    this.chatUiUpdateEvent.emit({
      type: 'TAB_CHANGE',
      tab,
      chatIndex: index,
      chatUiModel: ChatUiModelModifiers.changeTabInResponseByIndex(this.chatUiModel(), index, tab),
    });
  }

  submitFeedback(rawData: T, index: number, feedback: TChatUiModelFeedback) {
    this.chatUiUpdateEvent.emit({ type: 'FEEDBACK_SUBMIT', feedback, rawData, chatIndex: index });
  }

  quickQuestionClicked(query: string) {
    this.chatUiModel.update((prevModel) => ChatUiModelModifiers.updateQueryInModel(prevModel, query));
  }

  generateInsights(rawData: T, chatIndex: number) {
    this.chatUiModel.update((prevModel) =>
      ChatUiModelModifiers.updateInsightsInfoForTableChartResponseByIndex(prevModel, chatIndex, {
        status: 'GENERATING',
      }),
    );

    this.chatUiUpdateEvent.emit({ type: 'GENERATE_INSIGHTS', rawData, chatIndex, chatUiModel: this.chatUiModel() });
  }

  viewTable(tableModel: TTableTreeModel, rawData: T) {
    this.chatUiUpdateEvent.emit({ type: 'VIEW_TABLE', tableModel, rawData });
  }

  viewChart(chartModel: Highcharts.Options) {
    this.chatUiUpdateEvent.emit({ type: 'VIEW_CHART', chartModel });
  }

  downloadTableData(rawData: T) {
    this.chatUiUpdateEvent.emit({ type: 'DOWNLOAD_TABLE_DATA', rawData });
  }

  trackByChats = (index: number, item: TChatUiModel['chats'][number]) => index + item.type;
}
